import type { ChordQuality, LayoutCode } from "@/lib/virtuosa-layout/types";

// The tutorial teaches a law, not a map.
//
// The previous version presented three generating vectors — a column right is
// +3, the up-right diagonal is +4, the up-left diagonal is +1 — as three
// independent facts to remember. There are only two: +4 *is* a column plus a
// row. Stating the third separately costs a third of the memory budget for
// nothing, and hides that every interval on this keyboard is one arithmetic:
//
//     interval = 3 × colonnes + rangées
//
// From which the octave (four columns) and the whole tone (two rows) fall out
// free, instead of being two more facts. Both were previously untaught.
//
// It also now anchors on f and j rather than on d. d = C♯3 is the lattice's
// origin, which is a fact about the model; f and j carry the moulded bumps and
// are the only two keys a hand finds without looking. An anchor you can feel
// beats an anchor that is conceptually central.

export type TutorialStep =
  | {
      kind: "info";
      id: string;
      title: string;
      description: string;
      /** Cells to light while the text is read, without asking for anything. */
      hintCodes?: LayoutCode[];
      /** Wash the cells by pitch-class family for this step. */
      showFamilies?: boolean;
    }
  | {
      kind: "press-code";
      id: string;
      title: string;
      description: string;
      hintCodes: LayoutCode[];
      target: LayoutCode;
      /** Cells shown as the place the move starts from. */
      fromCodes?: LayoutCode[];
    }
  | {
      kind: "play-sequence";
      id: string;
      title: string;
      description: string;
      hintCodes: LayoutCode[];
      midis: number[];
      noteNames: string[];
    }
  | {
      kind: "press-chord";
      id: string;
      title: string;
      description: string;
      rootSemi: number;
      quality: ChordQuality;
      withBass: boolean;
      /** Cells to call out as the one that moved, relative to the step before. */
      movedCodes?: LayoutCode[];
    }
  | {
      kind: "chord-sweep";
      id: string;
      title: string;
      description: string;
      quality: ChordQuality;
      withBass: boolean;
    };

export const TUTORIAL_STEPS: TutorialStep[] = [
  {
    kind: "info",
    id: "welcome",
    title: "Ton clavier est un instrument",
    description:
      "Pas un raccourci vers un piano : un instrument à part entière, avec ses propres règles. Il y en a deux, et tout le reste s'en déduit. Cinq minutes.",
  },
  {
    kind: "press-code",
    id: "landmarks",
    title: "D'abord, les repères",
    description:
      "Pose tes index sur f et j — les deux touches à bosse, les seules que tes doigts trouvent sans regarder. Tes deux mains sont déjà posées sur un accord : q s d f et j k l m empilent les mêmes quatre notes, de tierce mineure en tierce mineure. Joue f.",
    hintCodes: ["KeyF", "KeyJ"],
    target: "KeyF",
  },
  {
    kind: "press-code",
    id: "column",
    title: "Règle 1 — une colonne à droite = +3",
    description:
      "Une case vers la droite monte de trois demi-tons, une tierce mineure. Depuis f (Mi), la touche juste à droite donne Sol.",
    hintCodes: ["KeyG"],
    fromCodes: ["KeyF"],
    target: "KeyG",
  },
  {
    kind: "press-code",
    id: "row",
    title: "Règle 2 — une rangée au-dessus = +1",
    description:
      "Une rangée vers le haut monte d'un demi-ton. Depuis f (Mi), la touche juste au-dessus donne Fa. C'est la deuxième et dernière règle.",
    hintCodes: ["KeyR"],
    fromCodes: ["KeyF"],
    target: "KeyR",
  },
  {
    kind: "press-code",
    id: "octave",
    title: "Tout le reste se calcule",
    description:
      "3 × colonnes + rangées, et tu as n'importe quel intervalle. L'octave, c'est douze : quatre colonnes à droite, sans changer de rangée. Depuis f, va chercher le Mi du dessus.",
    hintCodes: ["KeyK"],
    fromCodes: ["KeyF"],
    target: "KeyK",
  },
  {
    kind: "info",
    id: "families",
    title: "Une note, une rangée",
    description:
      "Il n'y a pas 46 cases à retenir, il y a trois familles. Do, Ré♯, Fa♯ et La vivent sur la rangée des chiffres et celle du bas — les mêmes notes, deux fois, c'est pour ça que certaines ont deux touches. Ré, Fa, Sol♯ et Si sur la rangée du haut. Do♯, Mi, Sol et La♯ sur celle de tes doigts. Chercher une note, c'est d'abord choisir une rangée.",
    showFamilies: true,
  },
  {
    kind: "press-code",
    id: "whole-tone",
    title: "Le ton, et son repli",
    description:
      "L'intervalle le plus fréquent en musique monte de deux demi-tons : deux rangées vers le haut. Depuis f (Mi), tu arrives sur Fa♯. Et quand il n'y a pas deux rangées au-dessus, le ton se replie : une rangée en dessous, une colonne à droite. C'est ça, le zigzag des gammes.",
    hintCodes: ["Digit4"],
    fromCodes: ["KeyF"],
    target: "Digit4",
  },
  {
    kind: "play-sequence",
    id: "c-major-scale",
    title: "Une gamme, c'est ton ton demi-ton",
    description:
      "Do majeur : ton, ton, demi-ton, ton, ton, ton, demi-ton. Le demi-ton monte d'une rangée, le ton de deux — ou se replie quand il n'y a plus de place. Suis les touches.",
    hintCodes: ["KeyC", "KeyE", "KeyF", "KeyR", "KeyG", "KeyN", "KeyY", "Digit6"],
    midis: [48, 50, 52, 53, 55, 57, 59, 60],
    noteNames: ["C3", "D3", "E3", "F3", "G3", "A3", "B3", "C4"],
  },
  {
    kind: "press-chord",
    id: "c-major-chord",
    title: "Un accord majeur, une seule forme",
    description:
      "La basse à gauche, puis quatre colonnes à droite pour la fondamentale, et les deux notes suivantes sur la rangée du dessus. Joue les quatre touches, dans l'ordre que tu veux.",
    rootSemi: 0,
    quality: "maj",
    withBass: true,
  },
  {
    kind: "press-chord",
    id: "c-minor-chord",
    title: "Mineur : une touche descend",
    description:
      "Passer en mineur, c'est déplacer la tierce d'une seule rangée vers le bas. Même colonne, une rangée plus bas. Rien d'autre ne bouge — et c'est vrai dans les douze tonalités.",
    rootSemi: 0,
    quality: "min",
    withBass: true,
    movedCodes: ["Comma"],
  },
  {
    kind: "chord-sweep",
    id: "invariance",
    title: "Et maintenant, regarde",
    description:
      "La même forme, sur les douze tonalités, sans rien changer. C'est ce qu'un piano ne fait pas : là-bas, chaque tonalité a son doigté. Ici tu apprends le geste une fois.",
    quality: "maj",
    withBass: true,
  },
  {
    kind: "info",
    id: "done",
    title: "Voilà, tu as tout",
    description:
      "Deux règles — colonne +3, rangée +1 — et trois familles. Un accord : un seul geste pour les douze tonalités, et une touche pour passer de majeur à mineur. Une gamme : trois formes, une par famille de tonique, parce que le ton se replie en haut du clavier. Le sélecteur d'accord et de gamme est là pour vérifier, pas pour lire.",
  },
];
